"use client";

import {
  ChevronDownIcon,
  MenuIcon,
  SearchIcon,
  UserIcon,
  XIcon,
} from "@/utils/icons";
import Link from "next/link";
import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { hasPermission } from "@/utils/permissions";
import { usePathname, useRouter } from "next/navigation";
import React from "react";
import Image from "next/image";

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL;

const apiFetch = async (url: string) => {
  const fullUrl = `${API_BASE_URL}${url}`;

  console.log(`Fetching from: ${fullUrl}`);
  const res = await fetch(fullUrl);

  if (!res.ok) {
    console.error(`API Fetch failed with status: ${res.status} for URL: ${fullUrl}`);
    // Attempt to read error message if provided by the backend
    try {
      const errorData = await res.json();
      throw new Error(errorData.message || `API fetch failed with status ${res.status}`);
    } catch {
      throw new Error(`API fetch failed with status ${res.status}`);
    }
  }
  return res.json();
};

// --- INTERFACES for Search Results ---
interface PopulatedStore {
  name: string;
  slug: string;
  icon?: string;
}

interface SearchItem {
  _id: string;
  name?: string;
  title?: string;
  slug: string;
  icon?: string;
  storeIcon?: string;
  store?: PopulatedStore | string;
}

interface SearchData {
  stores: SearchItem[];
  categories: SearchItem[];
  offers: SearchItem[];
}

const navLinks = [
  { name: "Stores", href: "/store/", badge: "300+", hasDropdown: true },
  { name: "Deals", href: "/offers/", badge: "2700+", hasDropdown: true },
  { name: "Categories", href: "/category/", hasDropdown: true },
  // { name: "Blogs", href: "/blogs/", hasDropdown: false },
];

// --- Spinner Component ---
const Spinner = () => (
  <svg className="animate-spin h-5 w-5 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);


export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchData | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [hideMenuButton, setHideMenuButton] = useState(false);

  const { user, logout, loading } = useAuth();
  const router = useRouter();
  const pageLocation = usePathname();
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  //  Logic to set hideMenuButton state
  useEffect(() => {
    if (pageLocation?.includes('/admin')) {
      setHideMenuButton(true);
      if (mobileOpen) {
        setMobileOpen(false);
      }
    } else {
      setHideMenuButton(false);
    }
  }, [pageLocation, mobileOpen]);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  // Debounce logic
  useEffect(() => {
    if (searchTerm.length < 2) {
      setSearchResults(null);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);

    const delayDebounceFn = setTimeout(async () => {
      try {
        const response = await apiFetch(`/search/unified/?q=${encodeURIComponent(searchTerm)}`);

        if (response.success && response.data) {
          setSearchResults(response.data as SearchData);
        } else {
          setSearchResults({ stores: [], categories: [], offers: [] });
        }
      } catch (error) {
        console.error("Live search failed:", (error as Error).message);
        setSearchResults(null);
      } finally {
        setSearchLoading(false);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  // Close dropdowns on outside click (Desktop only for search)
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        profileRef.current && !profileRef.current.contains(event.target as Node)
      ) {
        setProfileOpen(false);
      }

      // Desktop search close logic
      if (
        searchRef.current && !searchRef.current.contains(event.target as Node) && !mobileOpen
      ) {
        setSearchFocused(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [mobileOpen]);


  // FINAL Search Submission Handler ---
  const handleFinalSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const term = searchTerm.trim();

    if (term && term.length >= 2) {

      if (mobileOpen) {
        // setSearchFocused(false); 
      } else {
        setSearchFocused(true);
      }
    }
  };
  // Function to handle link click from the dropdown
  const handleResultClick = (href: string) => {
    setSearchTerm('');
    setSearchResults(null);
    setSearchFocused(false);
    setMobileOpen(false);
    router.push(href+"/");
  };

  if (loading) {
    return (
      <nav className="backdrop-blur-2xl bg-slate-50/10 shadow-lg sticky top-0 z-50 border-b border-gray-200 h-16 flex items-center justify-between px-4 animate-pulse">
        <div className="bg-gray-200 rounded w-32 h-8" />
        <div className="flex items-center space-x-4">
          <div className="bg-gray-200 rounded-full w-8 h-8" />
          <div className="bg-gray-200 rounded-full w-8 h-8" />
        </div>
      </nav>
    );
  }

  // Helper component for rendering search items
  const SearchResultSection = ({
    title,
    items,
    hrefPrefix,
    itemKey,
    mobileItemColumns = 1,
  }: {
    title: string,
    items: SearchItem[],
    hrefPrefix: string,
    itemKey: 'name' | 'title',
    mobileItemColumns?: number,
    allStores?: SearchItem[]
  }) => {
    const displayItems = items;

    if (displayItems.length === 0) return null;
    const isMobileLayout = mobileOpen;

    let gridClass = 'grid-cols-2 px-3';

    if (isMobileLayout) {
      gridClass = mobileItemColumns === 2 ? 'grid-cols-2' : 'grid-cols-1';
    }

    return (
      <div className={`mb-4 ${isMobileLayout ? 'px-0' : ''}`}>
        <h3 className={`text-xs font-bold text-gray-500 uppercase ${isMobileLayout ? 'px-0' : 'px-4'} mb-2`}>{title} ({items.length})</h3>
        {/* Conditional grid for desktop vs. mobile menu view */}
        <div className={`grid gap-2 ${gridClass}`}>
          {displayItems.map((item) => {
            let itemIconSrc: string | undefined = undefined;

            if (title === 'Deals') {
              itemIconSrc = item.storeIcon;
              if (!itemIconSrc && typeof item.store === 'object' && item.store !== null) {
                itemIconSrc = item.store.icon;
              }
            } else {
              // For Stores and Categories, use the item's own 'icon' field. This logic is correct.
              itemIconSrc = item.icon;
            }

            return (
              <div
                key={item._id}
                className="bg-gray-50 p-2 rounded-lg hover:bg-primary-50 transition-colors cursor-pointer border border-gray-100"
                onClick={() => handleResultClick(`${hrefPrefix}/${item.slug}/`)}
              >
                {/* Item Icon */}
                <div className="flex items-center space-x-2">
                  {itemIconSrc ? (
                    <Image
                      height={32}
                      width={32}
                      src={itemIconSrc}
                      alt={item[itemKey] || 'Icon'}
                      className="w-8 h-8 object-contain"
                    />
                  ) : (
                    <div className="w-8 h-8 bg-gray-300 rounded-sm"></div>
                  )}
                  <p className="text-sm font-medium text-gray-800 truncate">{item[itemKey]}</p>
                </div>
                {/* Store name for offers */}
                {item.store && title === 'Deals' && (
                  <p className="text-xs text-gray-500 mt-0.5 pl-10">
                    from {
                      typeof item.store === 'object' && item.store.name
                        ? item.store.name
                        : String(item.store).replace(/-offers$/, '')
                    }
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };


  const totalResults = (searchResults?.stores.length || 0) + (searchResults?.categories.length || 0) + (searchResults?.offers.length || 0);

  // --- Main Navbar Component Render ---
  return (
    <>
      <nav className="backdrop-blur-2xl bg-slate-50 shadow-lg sticky top-0 z-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-2">
            {/* Logo */}
            <div className="flex items-center">
              <Link href="/" className="flex items-center space-x-2">
              {process.env.NEXT_PUBLIC_APP_TITLE === "OfferCounty" ? 

                <div className="flex items-center space-x-2">
                  <div className="relative">
                    <Image
                      src="/favicon.ico"
                      alt="Logo"
                      width={40}
                      height={40}
                      className="w-10 h-10 object-contain"
                    />
                  </div>
                  <div>
                    <span className="text-xl font-bold bg-gradient-to-r from-primary-500 to-purple-500 bg-clip-text text-transparent">
                      {process.env.NEXT_PUBLIC_APP_TITLE}
                    </span>
                    <div className="text-xs text-gray-400">
                      {process.env.NEXT_PUBLIC_APP_SLOGAN}
                    </div>
                  </div>
                </div>
                :
                <div className="flex flex-col items-center space-x-2">
                  <div className="flex">
                  <Image src="/logo_dark2.ico" alt="Logo" className="object-contain" width={130} height={50} />
               <div className="text-primary-500 text-[17px] font-extralight place-self-end">tweak</div>

                  </div>
                  <div>
                    {/* <span className="text-xl font-bold bg-gradient-to-r from-primary-500 to-purple-500 bg-clip-text text-transparent">
                      {process.env.NEXT_PUBLIC_APP_TITLE}
                    </span> */}
                    <div className="text-xs text-gray-400">
                      {process.env.NEXT_PUBLIC_APP_SLOGAN}
                    </div>
                  </div>
                </div>
                }
              </Link>
              
            </div>

            {/* Navigation links - Desktop */}
            <div className="hidden md:flex items-center justify-center py-2 mx-6">
              <div className="flex items-center space-x-8">
                {navLinks.map((link) => (
                  <div key={link.name} className="relative group">
                    <Link
                      href={link.href+'/'}
                      className="flex items-center space-x-1 text-gray-700 hover:text-primary-600 text-sm font-semibold transition-colors py-2"
                    >
                      <span>{link.name}</span>
                    </Link>
                  </div>
                ))}
              </div>
            </div>

            {/* Search bar - Desktop with Dropdown */}
            <div className="hidden md:flex flex-1 max-w-lg relative" ref={searchRef}>
              <form onSubmit={handleFinalSearch} className="relative w-full">
                <input
                  type="text"
                  name="search-input"
                  placeholder="Search deals, store"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full px-4 py-1.5 border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-50 text-sm bg-gray-50 transition-all ${searchFocused
                    ? "shadow-lg border-primary-500"
                    : "border-gray-300"
                    }`}
                  onFocus={() => setSearchFocused(true)}
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-primary-500 text-white px-3 py-1 rounded-lg text-sm hover:bg-primary-600 transition-colors"
                  aria-label="Search"
                >
                  <SearchIcon className="h-4 w-4" />
                </button>
              </form>

              {/* Search Results Dropdown (Desktop) */}
              {searchFocused && searchTerm.length >= 2 && (
                <div className="absolute top-full mt-1 w-[650px] right-0 bg-white rounded-xl shadow-2xl border border-gray-200 z-[60] p-3 max-h-[80vh] overflow-y-auto">
                  {searchLoading ? (
                    <div className="flex items-center justify-center p-6 text-primary-600">
                      <Spinner /> <span className="ml-3">Loading...</span>
                    </div>
                  ) : (
                    <>
                      {totalResults > 0 ? (
                        <>
                          <SearchResultSection title="Stores" items={searchResults?.stores || []} hrefPrefix="/store" itemKey="name" />
                          <SearchResultSection title="Categories" items={searchResults?.categories || []} hrefPrefix="/category" itemKey="name" />

                          <SearchResultSection
                            title="Deals"
                            items={searchResults?.offers || []}
                            hrefPrefix="/offers"
                            itemKey="title"
                          />
                        </>
                      ) : (
                        <p className="p-4 text-center text-gray-500 text-sm">No results found for **&quot;{searchTerm}&quot;**.</p>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Right section (User/Login, Mobile Menu Toggle) */}
            <div className="flex items-center ">
              {/* Country selector */}
              <div className="hidden items-center space-x-1 text-sm">
                <span className="w-6 h-4 bg-gradient-to-b from-orange-500 via-white to-green-500 rounded-sm"></span>
                <span className="text-gray-700">IN</span>
              </div>

              {/* User account */}
              {user ? (
                <div className="relative" ref={profileRef}>
                  <button
                    className="flex items-center bg-primary-100 text-primary-700 px-2 py-2 rounded-lg hover:bg-primary-200 transition-colors focus:outline-none"
                    onClick={() => setProfileOpen((v) => !v)}
                    aria-label="Profile menu"
                  >
                    <UserIcon className="h-4 w-4" />
                    <ChevronDownIcon className="h-3 w-3" />
                  </button>
                  {profileOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                      <div className="px-4 py-3 border-b border-gray-100">
                        <div className="font-semibold text-gray-800">
                          {user.username}
                        </div>
                        <div className="text-xs text-gray-500">
                          {user.email}
                        </div>
                        <div className="text-xs font-semibold bg-primary-500 text-white rounded px-2 mt-1 inline-block">
                          {user.role}
                        </div>
                      </div>
                      <Link
                        href="/profile/"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setProfileOpen(false)}
                      >
                        My Profile
                      </Link>
                      <button
                        onClick={() => {
                          setProfileOpen(false);
                          logout();
                          router.push("/login/");
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 border-t border-gray-100"
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  href="/login/"
                  aria-label="Login"
                  className="hidden md:flex items-center space-x-2 bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors"
                >
                  <UserIcon className="h-4 w-4" />
                  <span className="text-xs font-medium">Log In / Sign Up</span>
                </Link>
              )}
              {/* Mobile menu button: Only show if NOT on an admin page */}
              {/* 🌟 CONDITIONAL RENDERING APPLIED HERE 🌟 */}
              <div className={` ${hideMenuButton ? 'px-5 py-2 w-8 h-6' : ''} `}>
                <button
                  className={` ${hideMenuButton ? 'hidden' : ''} md:hidden p-2 rounded-lg text-gray-600 hover:text-primary-600`}
                  onClick={() => setMobileOpen(!mobileOpen)}
                  aria-label="Toggle menu"
                >
                  {mobileOpen ? (
                    <XIcon className="h-6 w-6" />
                  ) : (
                    <MenuIcon className="h-6 w-6" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Navigation links - Desktop*/}
          {/* <div className="hidden flex items-center justify-center py-2 bg-primary-950">
            <div className="flex items-center space-x-8">
              {navLinks.map((link) => (
                <div key={link.name} className="relative group">
                  <Link
                    href={link.href}
                    className="flex items-center space-x-1 text-gray-700 hover:text-primary-600 text-sm font-semibold transition-colors py-2"
                  >
                    <span>{link.name}</span>
                    {link.badge && (
                      <span className="bg-primary-500 text-white text-xs px-2 py-0.5 rounded-lg">
                        {link.badge}
                      </span>
                    )}
                  </Link>
                </div>
              ))}
            </div>
          </div> */}
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 shadow-lg text-gray-700 max-h-[85vh] overflow-y-auto">

            {/* MOBILE SEARCH FORM */}
            <div className="px-4 py-3 border-b border-gray-100 sticky top-0 bg-white z-10">
              <form onSubmit={handleFinalSearch} className="relative w-full">
                <input
                  type="text"
                  name="search-input-mobile"
                  placeholder="Search deals, store and category"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  // Set searchFocused to true when mobile menu is open and input is focused
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setSearchFocused(false)}
                  className={`w-full px-4 py-1.5 border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 text-sm bg-gray-50 transition-all ${searchTerm.length > 0
                    ? "shadow-lg border-primary-500"
                    : "border-gray-300"
                    }`}
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-primary-500 text-white px-3 py-1 rounded-lg text-sm hover:bg-primary-600 transition-colors"
                  aria-label="Search"
                >
                  <SearchIcon className="h-4 w-4" />
                </button>
              </form>
            </div>

            {/* MOBILE SEARCH RESULTS */}
            {(searchTerm.length >= 2 || totalResults > 0) && (
              <div className="p-2">
                {searchLoading ? (
                  <div className="flex items-center justify-center p-6 text-primary-600">
                    <Spinner /> <span className="ml-3">Loading...</span>
                  </div>
                ) : (
                  <>
                    {totalResults > 0 ? (
                      <div className="flex flex-col gap-4">
                        {/* Mobile: Stores in 2 columns */}
                        <SearchResultSection title="Stores" items={searchResults?.stores || []} hrefPrefix="/store" itemKey="name" mobileItemColumns={2} />

                        {/* Mobile: Categories & Deals in 1 column (default) */}
                        <SearchResultSection title="Categories" items={searchResults?.categories || []} hrefPrefix="/category" itemKey="name" />

                        <SearchResultSection
                          title="Deals"
                          items={searchResults?.offers || []}
                          hrefPrefix="/offers"
                          itemKey="title"
                        />
                      </div>
                    ) : (
                      <p className="p-4 text-center text-gray-500 text-sm">No results found for **&quot;{searchTerm}&quot;**.</p>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Mobile navigation links (Only show if search is not active or has no results) */}
            {searchTerm.length < 2 && (
              <div className="px-4 py-2">
                {navLinks.map((link) => (
                  <div
                    key={link.name}
                    className="border-b border-gray-100 last:border-b-0"
                  >
                    <Link
                      href={link.href+'/'}
                      className="flex items-center justify-between py-3 text-gray-700 hover:text-primary-600 transition-colors"
                      onClick={() => setMobileOpen(false)}
                    >
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">{link.name}</span>
                        {link.badge && (
                          <span className="bg-primary-500 text-white text-xs px-2 py-0.5 rounded-full">
                            {link.badge}
                          </span>
                        )}
                      </div>
                      {link.hasDropdown && (
                        <ChevronDownIcon className="h-4 w-4" />
                      )}
                    </Link>
                  </div>
                ))}
                {/* Profile/Login link for mobile */}
                {user ? (
                  <Link
                    href="/profile/"
                    className="block py-3 text-primary-700 font-bold hover:underline"
                    onClick={() => setMobileOpen(false)}
                  >
                    Profile
                  </Link>
                ) : (
                  <Link
                    href="/login/"
                    className="block py-3 text-primary-700 font-bold hover:underline"
                    onClick={() => setMobileOpen(false)}
                  >
                    Log In / Sign Up
                  </Link>
                )}
                {/* Admin links for mobile */}
                {user &&
                  (hasPermission(user.role, "analytics") ||
                    hasPermission(user.role, "create")) && (
                    <>
                      {hasPermission(user.role, "create") && (
                        <Link
                          href="/admin/"
                          className="block py-3 text-primary-700 font-bold hover:underline"
                          onClick={() => setMobileOpen(false)}
                        >
                          Admin Panel
                        </Link>
                      )}
                      {/* {hasPermission(user.role, "analytics") && (
                        <Link
                          href="/admin/analytics"
                          className="block py-3 text-primary-700 font-bold hover:underline"
                          onClick={() => setMobileOpen(false)}
                        >
                          Analytics
                        </Link>
                      )}
                      {hasPermission(user.role, "create") && (
                        <>
                          <Link
                            href="/admin/coupons"
                            className="block py-3 text-primary-700 font-bold hover:underline"
                            onClick={() => setMobileOpen(false)}
                          >
                            Coupons
                          </Link>
                          <Link
                            href="/admin/stores"
                            className="block py-3 text-primary-700 font-bold hover:underline"
                            onClick={() => setMobileOpen(false)}
                          >
                            Stores
                          </Link>
                          <Link
                            href="/admin/categories"
                            className="block py-3 text-primary-700 font-bold hover:underline"
                            onClick={() => setMobileOpen(false)}
                          >
                            Categories
                          </Link>
                          <Link
                            href="/admin/users"
                            className="block py-3 text-primary-700 font-bold hover:underline"
                            onClick={() => setMobileOpen(false)}
                          >
                            Users
                          </Link>
                        </>
                      )} */}
                    </>
                  )}
              </div>
            )}
          </div>
        )}
      </nav>
    </>
  );
}